/**
 * Попытки загрузки договорной сметы на экране «Проверка КС-2/КС-3» (Z2,
 * аудит 35).
 *
 * Сервер может ответить 409 «нужно подтверждение» уже после того, как
 * сметчик сменил файл, договор или формат. Такой поздний ответ относится к
 * старой попытке: показать его — значит предложить «Всё равно загрузить»
 * для файла, который сервер не проверял. Поэтому:
 * - каждая отправка — неизменяемый снимок формы со своим номером попытки;
 * - ответ применяется, только если попытка последняя и форма не менялась;
 * - подтверждение отправляет именно тот снимок, о котором предупредил сервер.
 *
 * Модуль без React и псевдонимов путей: его проверяют тесты на node:test.
 */

/** Поля формы, от которых зависит проверка сервера. */
export interface EstimateUploadForm {
  contractId: string;
  file: File | null;
  format: string;
  isAmendment: boolean;
  comment: string;
}

/** Снимок формы на момент отправки. */
export interface EstimateUploadSnapshot {
  /** Номер попытки: ответ на старую попытку не применяется. */
  attempt: number;
  contractId: string;
  file: File;
  format: string;
  isAmendment: boolean;
  comment: string;
}

/** Предупреждение сервера вместе со снимком, который он проверял. */
export interface PendingConfirmation {
  snapshot: EstimateUploadSnapshot;
  message: string;
}

/** Счётчик попыток загрузки. Один на карточку загрузки. */
export class EstimateUploadAttempts {
  private last = 0;

  /** Новая попытка из текущей формы. `null` — нет договора или файла. */
  start(form: EstimateUploadForm): EstimateUploadSnapshot | null {
    const contractId = form.contractId.trim();
    if (!contractId || !form.file) return null;
    this.last += 1;
    return {
      attempt: this.last,
      contractId,
      file: form.file,
      format: form.format,
      isAmendment: form.isAmendment,
      comment: form.comment.trim(),
    };
  }

  /** Повторить подтверждённый снимок — новая попытка с тем же содержимым. */
  restart(snapshot: EstimateUploadSnapshot): EstimateUploadSnapshot {
    this.last += 1;
    return { ...snapshot, attempt: this.last };
  }

  /** Форма изменилась: ответы на уже отправленные попытки не применяются. */
  invalidate(): void {
    this.last += 1;
  }

  /** Ответ относится к последней попытке. */
  isCurrent(snapshot: EstimateUploadSnapshot): boolean {
    return snapshot.attempt === this.last;
  }
}

/** Снимок описывает то, что сейчас в форме: тот же договор, файл и формат. */
export function snapshotMatchesForm(
  snapshot: EstimateUploadSnapshot,
  form: EstimateUploadForm,
): boolean {
  return (
    snapshot.contractId === form.contractId.trim() &&
    snapshot.file === form.file &&
    snapshot.format === form.format
  );
}

/** Ответ сервера можно применить к экрану: попытка последняя, форма та же. */
export function isFreshResponse(
  attempts: EstimateUploadAttempts,
  snapshot: EstimateUploadSnapshot,
  form: EstimateUploadForm,
): boolean {
  return attempts.isCurrent(snapshot) && snapshotMatchesForm(snapshot, form);
}

/** Тело запроса загрузки — строго из снимка, а не из текущей формы. */
export function uploadFormData(snapshot: EstimateUploadSnapshot, confirm: boolean): FormData {
  const fd = new FormData();
  fd.append('file', snapshot.file);
  fd.append('contract_id', snapshot.contractId);
  fd.append('source_format', snapshot.format);
  if (snapshot.isAmendment) fd.append('is_amendment', 'true');
  if (snapshot.comment) fd.append('comment', snapshot.comment);
  if (confirm) fd.append('confirm_shrink', 'true');
  return fd;
}
